import { IntegrationModuleList } from 'common/types/kraken-core/IntegrationModule'
import { useEffect, useState } from 'react'
import { getIntegrationModule } from 'services/kraken-core/integration_module/integration.service'
import {
  Avatar,
  Box,
  Chip,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from '@mui/material'
import { Text } from '@loadsmart/loadsmart-ui'
import LSDate, { DATE_FORMAT_MMDDYYHHMM, DATE_FORMAT_MMDDYYHHMMSS } from 'common/Date.helpers'
import ClipboardLabel from 'atoms/ClipboardLabel'
import Loading from 'atoms/Loading'
import SimplePagination from 'atoms/SimplePagination'
import useDebouncedValue from 'hooks/useDebouncedValue'
import { PaginatedResponse } from 'common/types/kraken-core/Pagination'
import { InstallationList } from 'common/types/kraken-core/Installation'
import { searchInstallations } from 'services/kraken-core/installations/installations.service'
import { Widgets } from '@mui/icons-material'
import { getTraffic } from 'services/kraken-core/messages/messages.service'
import { TrafficResponseWrapper } from 'common/types/kraken-core/TrafficVolume'
import { toast } from 'react-toastify'
import moment from 'moment'
import MessageTrafficVolumeChart from 'molecules/MessageTraffic/MessageVolumeChart'
import MarkdownPreview from '@uiw/react-markdown-preview'

export interface IntegrationModuleDetailProps {
  id: string
}

const DEFAULT_TRAFFIC_PERIOD_IN_DAYS = 7
const DEFAULT_TRAFFIC_TIMEFRAME = 'hour'

export default function IntegrationModuleDetail(props: IntegrationModuleDetailProps) {
  const theme = useTheme()
  const [isLoadingModule, setIsLoadingModule] = useState<boolean>(false)
  const [isLoadingInstallations, setIsLoadingInstallations] = useState<boolean>(false)
  const [integrationModule, setIntegrationModule] = useState<IntegrationModuleList>()
  const [installations, setInstallations] = useState<PaginatedResponse<InstallationList>>()
  const [traffic, setTraffic] = useState<TrafficResponseWrapper>()
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(15)
  const [isExpanded, setIsExpanded] = useState<boolean>(false)
  const [moduleDocumentation, setModuleDocumentation] = useState<string | undefined>()
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 500)

  const toggleExpand = () => {
    setIsExpanded(!isExpanded)
  }

  function handleOnClickInstallation(installation: InstallationList) {
    window.open(`/installations/${installation.id}`, '_blank')
  }

  useEffect(() => {
    setIsLoadingModule(true)
    getIntegrationModule(props.id)
      .then(response => {
        response.response && setIntegrationModule(response.response.data)
      })
      .catch(error => {
        console.error('Error getting integration module')
      })
      .finally(() => {
        setIsLoadingModule(false)
      })
  }, [props.id])

  useEffect(() => {
    setPage(1)
  }, [debouncedSearchTerm, integrationModule?.id])

  useEffect(() => {
    if (!integrationModule) return

    setIsLoadingInstallations(true)
    searchInstallations({
      installation_type: ['kac', 'self_service'],
      integration_module_id: integrationModule.id,
      name: debouncedSearchTerm,
      page,
      page_size: pageSize,
    })
      .then(response => {
        response.response && setInstallations(response.response.data)
      })
      .catch(error => {
        console.error('Error listing installations')
      })
      .finally(() => {
        setIsLoadingInstallations(false)
      })
  }, [integrationModule?.id, debouncedSearchTerm, page, pageSize])

  useEffect(() => {
    if (integrationModule) {
      // Documentation
      // TODO: get documentation from integration module as soon as the API is ready
      setModuleDocumentation('')

      // Traffic
      setTraffic((prev: any) => ({ ...prev, isLoading: true }))
      getTraffic({
        filters: { integrationId: integrationModule.id },
        from_date: moment()
          .subtract(DEFAULT_TRAFFIC_PERIOD_IN_DAYS, 'days')
          .startOf('day')
          .toISOString(),
        to_date: '',
        timeframe: DEFAULT_TRAFFIC_TIMEFRAME,
      })
        .then(response => {
          response.response && setTraffic({ traffic: response.response.data, isLoading: false })
        })
        .catch(error => {
          toast.error('Error getting traffic')
        })
        .finally(() => {
          setTraffic((prev: any) => ({ ...prev, isLoading: false }))
        })
    }
  }, [integrationModule?.id])

  return (
    <Stack spacing={3}>
      {integrationModule ? (
        <>
          <Box flexDirection={'row'} display="flex" alignItems="center">
            <Avatar
              sizes="large"
              sx={{
                width: 60,
                height: 60,
                backgroundColor: 'secondary.main',
              }}
            >
              <Widgets fontSize={'large'} />
            </Avatar>
            <Stack spacing={1} pl={2}>
              <Typography variant="h1" fontWeight={'bold'}>
                {integrationModule?.name}
              </Typography>
              <Box>
                <Chip variant="outlined" size="small" label={integrationModule?.capability?.name} />
              </Box>
            </Stack>
          </Box>

          <Stack spacing={2}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Stack spacing={1}>
                  <Text variant="caption-bold" color="color-neutral">
                    ID
                  </Text>
                  <ClipboardLabel value={integrationModule.id} />
                </Stack>
              </Grid>
              <Grid item xs={12} md={3}>
                <Stack spacing={1}>
                  <Text variant="caption-bold" color="color-neutral">
                    Created At
                  </Text>
                  <ClipboardLabel
                    value={LSDate(integrationModule.created_at)?.format(DATE_FORMAT_MMDDYYHHMMSS)}
                  />
                </Stack>
              </Grid>
              <Grid item xs={12} md={3}>
                <Stack spacing={1}>
                  <Text variant="caption-bold" color="color-neutral">
                    Updated At
                  </Text>
                  <ClipboardLabel
                    value={LSDate(integrationModule.updated_at)?.format(DATE_FORMAT_MMDDYYHHMMSS)}
                  />
                </Stack>
              </Grid>
              <Grid item xs={12} md={12}>
                <Stack spacing={1}>
                  <Text variant="caption-bold" color="color-neutral">
                    Code
                  </Text>
                  <ClipboardLabel value={integrationModule.code} />
                </Stack>
              </Grid>
            </Grid>
          </Stack>

          <Stack spacing={2}>
            <Typography variant="h2" fontWeight={'bold'}>
              Documentation
            </Typography>
            {/* No documentation */}
            {!moduleDocumentation && (
              <Paper
                sx={{
                  height: 50,
                }}
              >
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography variant="body2">No documentation available</Typography>
                </Box>
              </Paper>
            )}

            {moduleDocumentation ? (
              <Paper
                sx={{
                  height: isExpanded ? 'auto' : 250,
                  overflow: 'hidden',
                  position: 'relative',
                }}
              >
                {/* Markdown documentation viewer */}
                {moduleDocumentation ? (
                  <div data-color-mode={theme.palette.mode}>
                    <MarkdownPreview
                      source={moduleDocumentation}
                      style={{
                        paddingTop: 20,
                        paddingBottom: 20,
                        paddingRight: 20,
                        paddingLeft: 25,
                      }}
                    />{' '}
                  </div>
                ) : null}

                {/* Show more / Show less button */}

                {!isExpanded && (
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      backdropFilter: 'blur(1px)',
                      width: '100%',
                      backgroundColor: 'rgba(255, 255, 255, 0.8)',
                      textAlign: 'center',
                      paddingBottom: '10px',
                      border: '1px solid rgba(255, 255, 255, 0.5)',
                      boxShadow: '0 0 20px rgba(255, 255, 255, 0.5)',
                    }}
                  >
                    <Typography
                      variant="body2"
                      color="primary"
                      sx={{ cursor: 'pointer', paddingLeft: 2, fontWeight: 'bold' }}
                      onClick={toggleExpand}
                    >
                      Show more
                    </Typography>
                  </Box>
                )}

                {isExpanded && (
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      width: '100%',
                      background: `linear-gradient(to top, ${theme.palette.background.paper}, transparent)`,
                      textAlign: 'center',
                      paddingBottom: '10px',
                    }}
                  >
                    <Typography
                      variant="body2"
                      color="primary"
                      sx={{ cursor: 'pointer', paddingLeft: 2 }}
                      onClick={toggleExpand}
                      fontWeight={'bold'}
                    >
                      Show less
                    </Typography>
                  </Box>
                )}
              </Paper>
            ) : null}
          </Stack>

          <Stack p={0} spacing={2}>
            <Typography variant="h2" fontWeight={'bold'}>
              Traffic
            </Typography>
            <Paper
              sx={{
                padding: 2,
              }}
            >
              {traffic && !traffic.isLoading && traffic.traffic.buckets.length > 0 ? (
                <MessageTrafficVolumeChart traffic={traffic} />
              ) : null}
              {traffic && traffic.traffic && traffic.traffic.buckets.length === 0 ? (
                <Box textAlign={'center'}>
                  <Typography variant="body2">
                    There has been no traffic in the last {DEFAULT_TRAFFIC_PERIOD_IN_DAYS} days...
                  </Typography>
                </Box>
              ) : null}

              {traffic && traffic.isLoading ? (
                <Box p={2}>
                  <Loading />
                </Box>
              ) : null}
            </Paper>
          </Stack>

          <Stack spacing={2} pt={0}>
            <Typography variant="h2" fontWeight={'bold'}>
              Installations
            </Typography>

            <TextField
              placeholder="Search by keyword..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              size="small"
            />

            {!isLoadingInstallations &&
            installations?.results &&
            installations?.results.length > 0 ? (
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <Text variant="caption-bold" color="color-neutral">
                          Name
                        </Text>
                      </TableCell>
                      <TableCell>
                        <Text variant="caption-bold" color="color-neutral">
                          Created At
                        </Text>
                      </TableCell>
                      <TableCell>
                        <Text variant="caption-bold" color="color-neutral">
                          Updated At
                        </Text>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {installations?.results.map((installation, index) => (
                      <TableRow
                        key={index}
                        onClick={() => handleOnClickInstallation(installation)}
                        sx={{
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.04)', // Adjust hover color as needed
                          },
                        }}
                      >
                        <TableCell>
                          <Typography variant="body2" fontWeight={'bold'}>
                            {installation.name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {LSDate(installation.created_at)?.format(DATE_FORMAT_MMDDYYHHMM)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {LSDate(installation.updated_at)?.format(DATE_FORMAT_MMDDYYHHMM)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : isLoadingInstallations ? (
              <Box p={2}>
                <Loading />
              </Box>
            ) : (
              <Box>
                <Typography variant="body2">There are no installations yet...</Typography>
              </Box>
            )}

            <Box display="flex" justifyContent="flex-end" width="100%">
              <SimplePagination
                currentPage={page}
                previousPage={() => setPage(prev => prev - 1)}
                nextPage={() => setPage(prev => prev + 1)}
                canPreviousPage={!!installations?.previous}
                canNextPage={!!installations?.next}
                onPage={p => setPage(p)}
                pageSize={pageSize}
                onPageSize={p => setPageSize(p)}
              />
            </Box>
          </Stack>
        </>
      ) : null}

      {isLoadingModule ? (
        <Box p={2}>
          <Loading />
        </Box>
      ) : null}
    </Stack>
  )
}
